import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/contexts/auth.context";
import { useInAppBrowser } from "@/lib/hooks/useInAppBrowser.hook";
import { Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { captureAuthException } from "@/lib/auth/captureAuthException";

interface GoogleSignInButtonProps {
	className?: string;
	variant?: "light" | "dark";
	text?: string;
	/** Called after a successful Google sign-in (before navigation). */
	onSuccess?: () => void;
	/** Extra disable, e.g. while another form action is running. */
	disabled?: boolean;
	/** Refresh Altcha after the popup succeeds (JWT upgrade). Do not gate the popup on this. */
	refreshCaptcha?: () => Promise<string>;
}

/**
 * Google Sign-In button following Google's brand guidelines
 * https://developers.google.com/identity/branding-guidelines
 *
 * @note it will also register the user if they don't exist yet
 *
 * @param className - Additional classes to apply to the button
 * @param variant - "light" (white background) or "dark" (blue background)
 * @param text - Button text, defaults to translation key value
 */
export const GoogleSignInButton = ({
	className = "",
	variant = "light",
	text,
	onSuccess,
	disabled = false,
	refreshCaptcha,
}: GoogleSignInButtonProps) => {
	const { t } = useTranslation("common");
	const { loginWithGoogle } = useAuth();
	const { isInAppBrowser, openInExternalBrowser } = useInAppBrowser();
	const [isBusy, setIsBusy] = useState(false);

	const handleGoogleSignIn = useCallback(async () => {
		if (isInAppBrowser) {
			openInExternalBrowser(window.location.href, "oauth");
			return;
		}

		setIsBusy(true);
		try {
			await loginWithGoogle(refreshCaptcha);
			onSuccess?.();
		} catch (error) {
			console.error("Google sign-in failed:", error);
			captureAuthException(error, "google_sign_in_button");
		} finally {
			setIsBusy(false);
		}
	}, [
		isInAppBrowser,
		loginWithGoogle,
		onSuccess,
		openInExternalBrowser,
		refreshCaptcha,
	]);

	return (
		<Button
			type="button"
			onClick={() => void handleGoogleSignIn()}
			className={`flex items-center justify-center gap-2 w-full ${
				variant === "light"
					? "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
					: "bg-[#4285F4] text-white hover:bg-[#3367d6]"
			} ${className}`}
			size="lg"
			disabled={disabled || isBusy}
			aria-busy={isBusy}
		>
			{isBusy ? (
				<>
					<Loader2 className="h-5 w-5 animate-spin" />
					<span>{t("signingIn")}</span>
				</>
			) : (
				<>
					<img src="/google-logo.svg" alt="Google logo" className="w-5 h-5" />
					<span>{text || t("login.continueWithGoogle")}</span>
				</>
			)}
		</Button>
	);
};
